/**
 * PUBLIC LANGUAGE SELECTOR COMPONENT
 *
 * Language selector for non-authenticated users browsing public pages.
 * Stores language preference in localStorage and uses i18next for real-time translation.
 *
 * Features:
 * - No authentication required
 * - Flag emojis and native language names
 * - Persistent selection (localStorage)
 * - Real-time language switching (no page reload)
 * - Uses i18next for translations
 * - Compact design for header/navbar
 * - Mobile responsive
 *
 * @version 7.4.0
 * @updated 2025-01-11
 */

import React, { useState, useEffect } from 'react';
import i18n from '../lib/i18n';
import { SUPPORTED_LANGUAGES, setStoredLanguage, isSupported } from '../lib/languageResolver';

export default function PublicLanguageSelector() {
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const [isChanging, setIsChanging] = useState<boolean>(false);

  // Follow the language already applied at startup, including a saved pref_language.
  useEffect(() => {
    const sync = (lng: string) => {
      const code = (lng || 'en').split('-')[0];
      setSelectedLanguage(isSupported(code) ? code : 'en');
    };
    sync(i18n.language);
    i18n.on('languageChanged', sync);
    return () => { i18n.off('languageChanged', sync); };
  }, []);

  /**
   * Handle language change
   */
  const handleLanguageChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLanguage = event.target.value;

    if (newLanguage === selectedLanguage || !isSupported(newLanguage)) {
      return;
    }

    setIsChanging(true);

    try {
      // Persist to the canonical localStorage key with explicit-choice flag.
      setStoredLanguage(newLanguage, { explicit: true });

      // Change language in i18next immediately (no page reload needed)
      await i18n.changeLanguage(newLanguage);

      setSelectedLanguage(newLanguage);

      setTimeout(() => {
        setIsChanging(false);
      }, 300);

    } catch (err) {
      console.error('Error updating language preference:', err);
      setIsChanging(false);
    }
  };

  return (
    <div className="public-language-selector">
      <select
        id="public-language-select"
        name="language"
        value={selectedLanguage}
        onChange={handleLanguageChange}
        disabled={isChanging}
        className="public-language-dropdown"
        aria-label="Select Language"
      >
        {SUPPORTED_LANGUAGES.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.flag} {lang.nativeName}
          </option>
        ))}
      </select>
    </div>
  );
}

